import * as React from 'react';
import {cn} from '../lib/cn';
import {CarouselContext} from './CarouselContext';

export type CarouselRootProps = React.ComponentPropsWithoutRef<'div'> & {
  index?: number;
  defaultIndex?: number;
  slideCount?: number;
  loop?: boolean;
  onIndexChange?: (index: number) => void;
};

export const CarouselRoot = React.forwardRef<HTMLDivElement, CarouselRootProps>(
  (
    {
      className,
      index: controlledIndex,
      defaultIndex = 0,
      slideCount: controlledSlideCount,
      loop = false,
      onIndexChange,
      children,
      ...props
    },
    ref,
  ) => {
    const [uncontrolledIndex, setUncontrolledIndex] = React.useState(defaultIndex);
    const [registeredSlideCount, setRegisteredSlideCount] = React.useState(0);

    const slideCount = controlledSlideCount ?? registeredSlideCount;
    const isControlled = controlledIndex !== undefined;
    const index = isControlled ? controlledIndex : uncontrolledIndex;
    const maxIndex = Math.max(0, slideCount - 1);
    const normalizedIndex = Math.min(Math.max(index, 0), maxIndex);
    const canGoPrevious = loop ? slideCount > 1 : normalizedIndex > 0;
    const canGoNext = loop ? slideCount > 1 : normalizedIndex < maxIndex;

    const setIndex = React.useCallback(
      (nextIndex: number) => {
        const clampedIndex = Math.min(Math.max(nextIndex, 0), maxIndex);

        if (!isControlled) {
          setUncontrolledIndex(clampedIndex);
        }

        onIndexChange?.(clampedIndex);
      },
      [isControlled, maxIndex, onIndexChange],
    );

    const goToPrevious = React.useCallback(() => {
      if (!canGoPrevious) {
        return;
      }

      setIndex(normalizedIndex === 0 ? maxIndex : normalizedIndex - 1);
    }, [canGoPrevious, maxIndex, normalizedIndex, setIndex]);

    const goToNext = React.useCallback(() => {
      if (!canGoNext) {
        return;
      }

      setIndex(normalizedIndex === maxIndex ? 0 : normalizedIndex + 1);
    }, [canGoNext, maxIndex, normalizedIndex, setIndex]);

    const registerSlide = React.useCallback(() => {
      setRegisteredSlideCount(count => count + 1);

      return () => setRegisteredSlideCount(count => Math.max(0, count - 1));
    }, []);

    const contextValue = React.useMemo(
      () => ({
        index: normalizedIndex,
        slideCount,
        canGoPrevious,
        canGoNext,
        loop,
        setIndex,
        goToPrevious,
        goToNext,
        registerSlide,
      }),
      [
        normalizedIndex,
        slideCount,
        canGoPrevious,
        canGoNext,
        loop,
        setIndex,
        goToPrevious,
        goToNext,
        registerSlide,
      ],
    );

    return (
      <CarouselContext.Provider value={contextValue}>
        <div
          ref={ref}
          data-slot="carousel"
          className={cn('relative h-full w-full', className)}
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);

CarouselRoot.displayName = 'CarouselRoot';
